// =============================================
// tokenCheck — sprawdza po kolei kazda funkcje, ktorej panel uzywa z danym
// kontem Cloudflare, i mapuje wynik na 3 uprawnienia:
//   Zone:Read  = GET /zones dziala
//   DNS:Edit   = POST + DELETE rekordu TXT `_pbnsd-test` dziala
//   Pages:Edit = upload-token na istniejacym projekcie Pages (null = brak
//                projektu, nie da sie sprawdzic bez tworzenia)
//
// Czysta logika (deps wstrzykiwane) — testowalna bez sieci. Zastepuje
// dawna zakladke "Test" (CfTestTab, usunieta 2026-10-03).
//
// Zasady testu zapisu DNS:
//   * zona ZAWSZE ta sama: pierwsza alfabetycznie po `name` (stabilnie miedzy
//     uruchomieniami, zeby smieci nie laodwaly po roznych domenach),
//   * przed utworzeniem kasujemy osierocony `_pbnsd-test` z poprzedniego
//     nieudanego testu,
//   * DELETE w finally — po bledzie w polowie rekord nie zostaje.
// =============================================
import {
  verifyToken, listAccounts, listZones, listDnsRecords, createDnsRecord, deleteDnsRecord,
  type CfZone, type CfDnsRecord, type CfAccount, type CfDnsRecordInput,
} from "@/services/cloudflareService";
import { listPagesProjects, getUploadToken } from "@/services/staticSite/cfPagesApi";

export type TokenCheckId = "token" | "account" | "zones" | "dns_read" | "dns_write" | "pages_read" | "pages_write";
export type TokenCheckStatus = "running" | "ok" | "fail" | "skip";

export interface TokenCheckStep {
  id: TokenCheckId;
  label: string;
  what: string;
  status: TokenCheckStatus;
  message: string;
}

export interface TokenCheckPermissions {
  zoneRead: boolean;
  dnsEdit: boolean;
  /** null = nie do sprawdzenia (brak projektu Pages na koncie) */
  pagesEdit: boolean | null;
}

export interface TokenCheckSummary {
  steps: TokenCheckStep[];
  permissions: TokenCheckPermissions;
  /** Konta CF widoczne dla tokena — do auto-uzupelnienia account_id. */
  detectedAccountIds: string[];
}

export interface TokenCheckDeps {
  verifyToken: (token: string) => Promise<{ ok: boolean; error?: string }>;
  listAccounts: (token: string) => Promise<CfAccount[]>;
  listZones: (token: string) => Promise<CfZone[]>;
  listDnsRecords: (token: string, zoneId: string) => Promise<CfDnsRecord[]>;
  createDnsRecord: (token: string, zoneId: string, zoneName: string, input: CfDnsRecordInput) => Promise<CfDnsRecord>;
  deleteDnsRecord: (token: string, zoneId: string, recordId: string) => Promise<void>;
  listPagesProjects: (token: string, accountId: string) => Promise<Array<{ name: string }>>;
  getUploadToken: (token: string, accountId: string, project: string) => Promise<unknown>;
  now?: () => number;
}

export const defaultTokenCheckDeps: TokenCheckDeps = {
  verifyToken, listAccounts, listZones, listDnsRecords, createDnsRecord, deleteDnsRecord, listPagesProjects, getUploadToken,
};

export const TEST_TXT_NAME = "_pbnsd-test";

export const TOKEN_CHECK_STEPS: ReadonlyArray<Pick<TokenCheckStep, "id" | "label" | "what">> = [
  { id: "token", label: "Token", what: "czy Cloudflare przyjmuje token" },
  { id: "account", label: "Konto (Account ID)", what: "GET /accounts — czy zapisane Account ID zgadza się z tokenem" },
  { id: "zones", label: "Strefy (domeny)", what: "GET /zones — zakładka Domeny w CF (Zone:Read)" },
  { id: "dns_read", label: "DNS odczyt", what: "GET /zones/{id}/dns_records — zakładka Rekordy DNS" },
  { id: "dns_write", label: "DNS zapis", what: `POST + DELETE rekordu TXT ${TEST_TXT_NAME} — rekordy A, kreator Przenieś domeny (DNS:Edit)` },
  { id: "pages_read", label: "Cloudflare Pages odczyt", what: "GET /accounts/{id}/pages/projects — migracja stron" },
  { id: "pages_write", label: "Cloudflare Pages zapis", what: "upload-token na istniejącym projekcie — deploy w migracji (Pages:Edit)" },
];

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Deterministyczny wybor zony do testu zapisu: pierwsza alfabetycznie po name. */
export function pickTestZone(zones: CfZone[]): CfZone | undefined {
  return [...zones].sort((a, b) => a.name.localeCompare(b.name))[0];
}

export async function runTokenCheck(
  acc: { api_token: string; account_id?: string | null },
  onStep?: (step: TokenCheckStep) => void,
  deps: TokenCheckDeps = defaultTokenCheckDeps,
): Promise<TokenCheckSummary> {
  const steps: TokenCheckStep[] = TOKEN_CHECK_STEPS.map((s) => ({ ...s, status: "running", message: "" }));
  const set = (id: TokenCheckId, status: TokenCheckStatus, message = "") => {
    const step = steps.find((s) => s.id === id)!;
    step.status = status;
    step.message = message;
    onStep?.({ ...step });
  };
  const t = acc.api_token;
  let accountId = acc.account_id || "";
  let zones: CfZone[] = [];
  let detectedAccountIds: string[] = [];

  // 1. token
  try {
    const v = await deps.verifyToken(t);
    if (v.ok) set("token", "ok", "Cloudflare przyjmuje token");
    else set("token", "fail", v.error || "odrzucony");
  } catch (e) { set("token", "fail", errMsg(e)); }

  // 2. konto
  try {
    const list = await deps.listAccounts(t);
    detectedAccountIds = list.map((a) => a.id);
    if (!list.length) set("account", "fail", "Token nie widzi żadnego konta (brak Account Settings: Read)");
    else if (!accountId) set("account", "fail", `Brak Account ID w panelu. Token widzi: ${list.map((a) => a.id).join(", ")} — uzupełnij w „Edytuj”.`);
    else if (!list.some((a) => a.id === accountId)) set("account", "fail", `Zapisane Account ID (${accountId}) nie należy do tego tokena. Token widzi: ${list.map((a) => `${a.id} (${a.name})`).join(", ")}.`);
    else set("account", "ok", `${accountId} · ${list.find((a) => a.id === accountId)?.name || ""}`);
    if (!accountId && list.length === 1) accountId = list[0].id;
  } catch (e) { set("account", "fail", errMsg(e)); }

  // 3. strefy
  let zoneRead = false;
  try {
    zones = await deps.listZones(t);
    zoneRead = true;
    set("zones", "ok", `${zones.length} stref: ${zones.slice(0, 5).map((z) => z.name).join(", ")}${zones.length > 5 ? "…" : ""}`);
  } catch (e) { set("zones", "fail", errMsg(e)); }

  // 4/5. DNS — zawsze ta sama zona (pierwsza alfabetycznie)
  let dnsEdit = false;
  const zone = pickTestZone(zones);
  if (!zone) {
    set("dns_read", "skip", "brak stref do sprawdzenia");
    set("dns_write", "skip", "brak stref do sprawdzenia");
  } else {
    let existing: CfDnsRecord[] = [];
    try {
      existing = await deps.listDnsRecords(t, zone.id);
      set("dns_read", "ok", `${zone.name}: ${existing.length} rekordów`);
    } catch (e) { set("dns_read", "fail", errMsg(e)); }

    let createdId: string | null = null;
    try {
      // sprzatanie po poprzednim nieudanym tescie
      const orphanFqdn = `${TEST_TXT_NAME}.${zone.name}`.toLowerCase();
      for (const r of existing) {
        if (r.type === "TXT" && r.name.toLowerCase() === orphanFqdn) {
          await deps.deleteDnsRecord(t, zone.id, r.id);
        }
      }
      const stamp = (deps.now ?? Date.now)();
      const rec = await deps.createDnsRecord(t, zone.id, zone.name, { type: "TXT", name: TEST_TXT_NAME, content: `pbnsd-test-${stamp}`, ttl: 60 });
      createdId = rec.id;
      dnsEdit = true;
      set("dns_write", "ok", `${zone.name}: rekord TXT utworzony i usunięty`);
    } catch (e) {
      set("dns_write", "fail", `${errMsg(e)} — brak uprawnienia Zone → DNS: Edit`);
    } finally {
      if (createdId) {
        try { await deps.deleteDnsRecord(t, zone.id, createdId); }
        catch (e) { set("dns_write", "fail", `rekord TXT utworzony, ale NIE usunięty (${errMsg(e)}) — skasuj ręcznie ${TEST_TXT_NAME}.${zone.name}`); dnsEdit = false; }
      }
    }
  }

  // 6/7. Pages
  let pagesEdit: boolean | null = null;
  if (!accountId) {
    set("pages_read", "skip", "brak Account ID");
    set("pages_write", "skip", "brak Account ID");
  } else {
    let projects: Array<{ name: string }> = [];
    try {
      projects = await deps.listPagesProjects(t, accountId);
      set("pages_read", "ok", `${projects.length} projektów Pages na koncie`);
    } catch (e) { set("pages_read", "fail", errMsg(e)); }
    if (!projects.length) {
      set("pages_write", "skip", "brak projektu Pages — zapis sprawdzi się przy pierwszym deployu (utworzenie projektu)");
    } else {
      try {
        await deps.getUploadToken(t, accountId, projects[0].name);
        pagesEdit = true;
        set("pages_write", "ok", `upload-token dla projektu ${projects[0].name} OK`);
      } catch (e) {
        pagesEdit = false;
        set("pages_write", "fail", `${errMsg(e)} (jeśli błąd mówi o uprawnieniach: brak Account → Cloudflare Pages: Edit)`);
      }
    }
  }

  return { steps, permissions: { zoneRead, dnsEdit, pagesEdit }, detectedAccountIds };
}
